import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Sprint 11.6 Block 2 — Task 1: CoachingAuftragModal
// 4-Fragen-Multi-Select-Wizard. Triggert nach Quick-Check + Registrierung,
// öffnet beim ersten Cockpit-Besuch, wenn user.fokustyp_primary gesetzt ist
// aber kein coaching_auftrag existiert.
//
// Backend:
//  - GET  /api/onboarding/coaching-auftrag/config   (4 Fragen + FT-Optionen)
//  - GET  /api/onboarding/coaching-auftrag          (Existenz-Check)
//  - POST /api/onboarding/coaching-auftrag          (Save: {areas, feelings, players, wishes})
//
// LocalStorage `f4s_coaching_auftrag_dismissed_v1` → wenn User "Später"
// klickt, fragen wir nicht sofort wieder (Server-Side ist es weiterhin offen).

const STORAGE_KEY = "f4s_coaching_auftrag_dismissed_v1";

function isDismissed() {
  try { return localStorage.getItem(STORAGE_KEY) === "true"; } catch (_) { return false; }
}
function markDismissed() {
  try { localStorage.setItem(STORAGE_KEY, "true"); } catch (_) { /* noop */ }
}

const QUESTION_KEYS = ["areas", "feelings", "players", "wishes"];

function ProgressDots({ step, total }) {
  return (
    <div className="flex items-center justify-center gap-1.5 mt-1" data-testid="coaching-auftrag-progress">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i === step ? "w-6 bg-[#4563BB]" : i < step ? "w-3 bg-[#4563BB]/50" : "w-3 bg-slate-200"
          }`}
        />
      ))}
    </div>
  );
}

export default function CoachingAuftragModal({ onClose, onSaved, open = false, openDelayMs = 0 }) {
  // Sprint M5.42 Block B (31.07.2026) — Teil D Mini-Fix:
  // Kein Auto-Open mehr beim Cockpit-Mount. Modal ist jetzt vollstaendig
  // controlled via `open`-Prop. Cockpit rendert einen expliziten Trigger
  // (Button/Nudge), erst dann oeffnet der Modal-Wizard.
  const [ready, setReady] = useState(false);
  const [show, setShow] = useState(false);
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState({ areas: [], feelings: [], players: [], wishes: [] });
  const [saving, setSaving] = useState(false);

  // Load config + check if user already submitted — nur wenn open=true.
  useEffect(() => {
    if (!open) {
      setShow(false);
      return () => {};
    }
    let cancelled = false;
    Promise.all([
      api.get("/onboarding/coaching-auftrag/config"),
      api.get("/onboarding/coaching-auftrag").catch(() => ({ data: { auftrag: null } })),
    ])
      .then(([cfgRes, existsRes]) => {
        if (cancelled) return;
        const existing = existsRes?.data?.auftrag;
        if (existing) {
          setReady(true);
          setShow(false);
          if (onClose) onClose();
          return;
        }
        setConfig(cfgRes.data);
        setReady(true);
        if (openDelayMs > 0) {
          setTimeout(() => {
            if (!cancelled) setShow(true);
          }, openDelayMs);
        } else {
          setShow(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReady(true);
          setShow(false);
        }
      });
    return () => { cancelled = true; };
  }, [open, openDelayMs, onClose]);

  const current = useMemo(() => {
    if (!config) return null;
    const q = config.questions || {};
    const key = QUESTION_KEYS[step];
    const optionsByKey = {
      areas: config.areas || [],
      feelings: config.feelings || [],
      players: config.players || [],
      wishes: config.wishes || [],
    };
    return {
      key,
      title: q[`q${step + 1}`] || "",
      max: q[`q${step + 1}_max`] || 3,
      options: optionsByKey[key] || [],
    };
  }, [config, step]);

  if (!ready || !show || !current) return null;

  const picked = picks[current.key];
  const togglePick = (opt) => {
    setPicks((prev) => {
      const list = prev[current.key];
      if (list.includes(opt)) {
        return { ...prev, [current.key]: list.filter((x) => x !== opt) };
      }
      if (list.length >= current.max) return prev;
      return { ...prev, [current.key]: [...list, opt] };
    });
  };

  const canAdvance = picked.length > 0;
  const isLast = step === QUESTION_KEYS.length - 1;

  const handleNext = () => {
    if (!canAdvance) return;
    if (isLast) {
      handleSave();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    setStep((s) => Math.max(0, s - 1));
  };

  const handleSkip = () => {
    markDismissed();
    setShow(false);
    if (onClose) onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/coaching-auftrag", picks);
      toast.success("Dein Coaching-Auftrag ist gespeichert.");
      markDismissed();
      setShow(false);
      if (onSaved) onSaved(picks);
      if (onClose) onClose();
    } catch (e) {
      toast.error("Speichern fehlgeschlagen — bitte später erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center px-4 py-6 overflow-y-auto"
        data-testid="coaching-auftrag-modal"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 relative shadow-2xl my-auto"
        >
          <button
            type="button"
            onClick={handleSkip}
            aria-label="Später"
            data-testid="coaching-auftrag-close"
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center mb-2">
            <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
              Mein Coaching-Auftrag
            </p>
            <h3 className="mt-1 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--fts-text-primary)]">
              Frage {step + 1} von {QUESTION_KEYS.length}
            </h3>
            <ProgressDots step={step} total={QUESTION_KEYS.length} />
          </div>

          <p
            className="mt-5 text-base font-semibold text-slate-800 leading-snug"
            data-testid="coaching-auftrag-question"
          >
            {current.title}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Bis zu {current.max} {current.max === 1 ? "Antwort" : "Antworten"} möglich
          </p>

          <div
            className="mt-5 space-y-2 max-h-[50vh] overflow-y-auto pr-1"
            data-testid="coaching-auftrag-options"
          >
            {current.options.map((opt, i) => {
              const active = picked.includes(opt);
              const disabled = !active && picked.length >= current.max;
              return (
                <button
                  key={`${current.key}-${i}`}
                  type="button"
                  onClick={() => togglePick(opt)}
                  disabled={disabled}
                  data-testid={`coaching-auftrag-option-${current.key}-${i}`}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-colors flex items-start gap-3 ${
                    active
                      ? "border-[#4563BB] bg-[#4563BB]/5"
                      : disabled
                        ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
                        : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                >
                  <span
                    className={`shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                      active ? "border-[#4563BB] bg-[#4563BB]" : "border-slate-300 bg-white"
                    }`}
                  >
                    {active && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="text-sm text-slate-800 leading-relaxed">{opt}</span>
                </button>
              );
            })}
          </div>

          <p className="mt-3 text-xs text-slate-400 text-right">
            {picked.length} / {current.max} ausgewählt
          </p>

          <div className="mt-5 flex gap-2">
            {step > 0 ? (
              <button
                type="button"
                onClick={handleBack}
                data-testid="coaching-auftrag-back"
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSkip}
                data-testid="coaching-auftrag-later"
                className="rounded-md border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Später
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              disabled={!canAdvance || saving}
              data-testid="coaching-auftrag-next"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#4563BB] hover:bg-[#3a55a5] disabled:opacity-50 text-white px-4 py-2.5 text-sm font-bold"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isLast ? (
                <>
                  Auftrag speichern
                  <Check className="w-4 h-4" />
                </>
              ) : (
                <>
                  Weiter
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
